import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { registerRow } from '@/hooks/useRegisterScroll'
import TableScroll from '@/components/TableScroll'
import clsx from 'clsx'
import { DELIVERY_COLUMNS, type DeliveryRow } from '@/lib/deliveryColumns'
import { paltrackWindowState } from '@/lib/nextPendingPaltrack'

// Excel-style column view for a list of deliveries — the "Table" alternative to
// the stacked cards on the Deliveries board, mirroring PalletTable. Columns
// whose value is empty for *every* row are dropped automatically, so the grid
// only shows the columns the current filter actually populates (no sea of
// em-dashes). Each row's received date links through to the delivery detail.

// The Next step cell is wide enough for its buttons, so they are never cut off
// inside the cell. It is NOT pinned: a right-pinned column covers the last data
// columns at scroll 0 and cuts a figure mid-digit ("12" reads as "1").
const ACTION_CELL = 'min-w-[180px]'

export default function DeliveriesTable({ rows, windowFromIso = null, renderAction }: {
  rows: DeliveryRow[]
  windowFromIso?: string | null
  /** UF-01: the row's next-step buttons (the same component the cards use). Adds a last "Next step" column. */
  renderAction?: (row: DeliveryRow) => ReactNode
}) {
  // Drop columns empty for every row, EXCEPT the leading 'received' column —
  // it's the row's link anchor, so it always renders.
  const cols = DELIVERY_COLUMNS.filter(
    c => c.id === 'received' || rows.some(r => c.value(r) != null),
  )

  return (
    <TableScroll className="-mx-4 px-4 pb-2">
      <table className="min-w-full text-xs border-collapse">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wider text-fg-muted">
            {cols.map(c => (
              <th
                key={c.id}
                className={clsx('px-2 py-2 font-semibold whitespace-nowrap', c.align === 'right' && 'text-right')}>
                {c.label}
              </th>
            ))}
            {renderAction && <th className="px-2 py-2 font-semibold whitespace-nowrap">Next step</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.delivery_id} {...registerRow(r.delivery_id)} className="group border-t hairline hover:bg-surface/40">
              {cols.map(c => {
                const v = c.value(r)
                // The first column is the row's link to the delivery detail.
                if (c.id === cols[0].id) {
                  return (
                    <td key={c.id} className="px-2 py-1.5 whitespace-nowrap">
                      {/* A 44px target: the row opened only through an 80×15 date link on a
                          phone (packhouse audit PM-DLV-09, PW-16). It is also the pinned column. */}
                      <Link
                        to={`/deliveries/${r.delivery_id}`}
                        state={paltrackWindowState(windowFromIso)}
                        className="inline-flex items-center min-h-[44px] font-semibold text-fg hover:text-blue-300 hover:underline">
                        {v ?? '—'}
                      </Link>
                    </td>
                  )
                }
                return (
                  <td
                    key={c.id}
                    className={clsx(
                      'px-2 py-1.5 text-fg-secondary',
                      c.align === 'right' ? 'text-right tabular-nums whitespace-nowrap' : 'whitespace-pre-line',
                      c.mono && 'font-mono',
                    )}>
                    {v ?? <span className="text-fg-muted">—</span>}
                  </td>
                )
              })}
              {renderAction && <td className={clsx('px-2 py-1.5', ACTION_CELL)}>{renderAction(r)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length === 0 && (
        <p className="text-fg-muted text-sm italic py-8 text-center">No deliveries match the current filters.</p>
      )}
    </TableScroll>
  )
}
